<template>
  <div
    role="main"
    aria-label="Statistiques et KPI du paiement livreur"
    tabindex="-1"
  >
    <!-- Header with Navigation -->
    <b-card
      class="mb-3"
      aria-label="En-tête et navigation des statistiques paiement livreur"
    >
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">
            <feather-icon
              icon="BarChart3Icon"
              class="mr-2"
            />
            Statistiques & KPI du Paiement #{{ id }}
          </h4>
          <p class="text-muted mb-0">
            Analyse détaillée des performances de livraison
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            :to="{ name: 'payment-show', params: { id } }"
            variant="outline-secondary"
            class="mr-2"
          >
            <feather-icon
              icon="PrinterIcon"
              size="16"
              class="mr-1"
            />
            Version Imprimable
          </b-button>
          <b-button
            :to="{ name: 'payment-list' }"
            variant="outline-primary"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="16"
              class="mr-1"
            />
            Retour à la liste
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5"
      aria-busy="true"
      aria-live="polite"
      aria-label="Chargement des statistiques du paiement livreur"
    >
      <b-spinner
        variant="primary"
        label="Chargement des données..."
        class="mb-3"
      />
      <p class="text-muted">
        Chargement des statistiques du paiement...
      </p>
    </div>

    <!-- Error State -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      class="mb-3"
      aria-live="assertive"
      aria-atomic="true"
      aria-label="Erreur lors du chargement des statistiques du paiement livreur"
    >
      <feather-icon
        icon="AlertCircleIcon"
        class="mr-2"
      />
      {{ error }}
    </b-alert>

    <!-- Payment not found -->
    <div
      v-if="!isLoading && !payment && !error"
      class="text-center py-5"
      aria-live="polite"
      aria-label="Aucun paiement livreur trouvé pour les statistiques"
      role="status"
    >
      <b-card
        class="shadow-sm"
        aria-label="Carte d'absence de statistiques pour le paiement livreur"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="48"
          class="text-warning mb-3"
        />
        <h5 class="text-muted mb-3">
          Paiement non trouvé
        </h5>
        <p class="text-muted mb-4">
          Le paiement avec l'ID {{ id }} n'existe pas ou vous n'avez pas les permissions pour le consulter.
        </p>
        <b-button
          :to="{ name: 'payment-list' }"
          variant="primary"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="16"
            class="mr-1"
          />
          Retour à la liste
        </b-button>
      </b-card>
    </div>

    <!-- Main Content -->
    <div
      v-if="payment && !isLoading"
      aria-label="Contenu principal des statistiques paiement livreur"
    >
      <!-- Basic Payment Info -->
      <b-row class="mb-4">
        <b-col>
          <b-card header="Informations du Paiement">
            <b-row>
              <b-col md="3">
                <div class="text-center">
                  <h3 class="text-primary">
                    #{{ payment.id }}
                  </h3>
                  <p class="text-muted mb-0">
                    Référence
                  </p>
                </div>
              </b-col>
              <b-col md="3">
                <div class="text-center">
                  <h5 class="mb-1">
                    {{ payment.delivery_man ? payment.delivery_man.username : 'N/A' }}
                  </h5>
                  <p class="text-muted mb-0">
                    Livreur
                  </p>
                </div>
              </b-col>
              <b-col md="3">
                <div class="text-center">
                  <h5 class="mb-1">
                    {{ payment.repository ? payment.repository.name : 'N/A' }}
                  </h5>
                  <p class="text-muted mb-0">
                    Dépôt
                  </p>
                </div>
              </b-col>
              <b-col md="3">
                <div class="text-center">
                  <b-badge
                    :variant="payment.is_validated ? 'success' : 'warning'"
                    class="h5 px-3 py-2"
                  >
                    <feather-icon
                      :icon="payment.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                      size="16"
                      class="mr-1"
                    />
                    {{ payment.is_validated ? 'Validé' : 'En attente' }}
                  </b-badge>
                  <p class="text-muted mb-0 mt-1">
                    Statut
                  </p>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Performance Metrics KPIs -->
      <b-row
        v-if="payment.performance_metrics"
        class="mb-4"
      >
        <b-col md="2">
          <b-card class="text-center bg-light-success kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="TrendingUpIcon"
                size="24"
                class="text-success mb-2"
              />
              <h3 class="text-success mb-1">
                {{ payment.performance_metrics.success_rate }}%
              </h3>
              <small class="text-muted">Taux de Succès</small>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="text-center bg-light-warning kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="RotateCcwIcon"
                size="24"
                class="text-warning mb-2"
              />
              <h3 class="text-warning mb-1">
                {{ payment.performance_metrics.return_rate }}%
              </h3>
              <small class="text-muted">Taux de Retour</small>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="text-center bg-light-info kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="RefreshCwIcon"
                size="24"
                class="text-info mb-2"
              />
              <h3 class="text-info mb-1">
                {{ payment.performance_metrics.exchange_rate }}%
              </h3>
              <small class="text-muted">Taux d'Échange</small>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="text-center bg-light-primary kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="DollarSignIcon"
                size="24"
                class="text-primary mb-2"
              />
              <h3 class="text-primary mb-1">
                {{ payment.performance_metrics.revenue_per_order.toFixed(2) }}
              </h3>
              <small class="text-muted">Revenue/Colis (DT)</small>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="text-center bg-light-secondary kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="TruckIcon"
                size="24"
                class="text-secondary mb-2"
              />
              <h3 class="text-secondary mb-1">
                {{ payment.performance_metrics.delivery_cost_per_order.toFixed(2) }}
              </h3>
              <small class="text-muted">Coût/Colis (DT)</small>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="text-center bg-light-danger kpi-card">
            <div class="card-body p-3">
              <feather-icon
                icon="ZapIcon"
                size="24"
                class="text-danger mb-2"
              />
              <h3 class="text-danger mb-1">
                {{ payment.performance_metrics.efficiency_score }}
              </h3>
              <small class="text-muted">Score Efficacité</small>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Detailed Order Breakdown Section -->
      <b-row class="mb-4">
        <!-- Delivered Orders Details -->
        <b-col md="4">
          <b-card
            header="Colis Livrés"
            header-bg-variant="success"
            header-text-variant="white"
          >
            <div class="mb-3">
              <h5 class="text-success">
                {{ payment.delivred_orders_count }} colis
                <small class="text-muted">({{ getOrderBreakdownValue('delivered_summary', 'total_value').toFixed(3) }} DT)</small>
              </h5>
            </div>

            <!-- Delivered Orders Table -->
            <b-table
              v-if="payment.delivred_orders && payment.delivred_orders.length > 0"
              :items="payment.delivred_orders"
              :fields="deliveredOrderFields"
              small
              striped
              hover
              responsive
              class="mb-3"
            >
              <template #cell(customer_info)="data">
                <div>
                  <strong>{{ data.item.customer_info.name }}</strong><br>
                  <small class="text-muted">{{ data.item.customer_info.phone1 }}</small>
                </div>
              </template>

              <template #cell(location)="data">
                <div>
                  {{ data.item.governorate_name }}<br>
                  <small class="text-muted">{{ data.item.delegation_name }}</small>
                </div>
              </template>

              <template #cell(price_ttc)="data">
                <b-badge variant="success">
                  {{ (data.item.price_ttc || 0).toFixed(3) }} DT
                </b-badge>
              </template>

              <template #cell(delivery_metrics)="data">
                <div v-if="data.item.order_metrics">
                  <small class="text-success">
                    <feather-icon
                      icon="ClockIcon"
                      size="12"
                      class="mr-1"
                    />
                    {{ data.item.order_metrics.delivery_time_hours || 'N/A' }}h
                  </small>
                  <br>
                  <small
                    v-if="data.item.order_metrics.is_fragile"
                    class="text-warning"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                      class="mr-1"
                    />
                    Fragile
                  </small>
                </div>
              </template>
            </b-table>

            <!-- Summary Stats -->
            <div class="border-top pt-2">
              <small class="text-muted">
                <feather-icon
                  icon="MapPinIcon"
                  size="12"
                  class="mr-1"
                />
                Gouvernorats: {{ getOrderBreakdownValue('delivered_summary', 'governorates').join(', ') || 'N/A' }}
              </small>
            </div>
          </b-card>
        </b-col>

        <!-- Returned Orders Details -->
        <b-col md="4">
          <b-card
            header="Colis Retournés"
            header-bg-variant="warning"
            header-text-variant="white"
          >
            <div class="mb-3">
              <h5 class="text-warning">
                {{ payment.return_orders_count }} colis
                <small class="text-muted">({{ getOrderBreakdownValue('returned_summary', 'total_value').toFixed(3) }} DT)</small>
              </h5>
            </div>

            <!-- Returned Orders Table -->
            <b-table
              v-if="payment.return_orders && payment.return_orders.length > 0"
              :items="payment.return_orders"
              :fields="returnOrderFields"
              small
              striped
              hover
              responsive
              class="mb-3"
            >
              <template #cell(customer_info)="data">
                <div>
                  <strong>{{ data.item.customer_info.name }}</strong><br>
                  <small class="text-muted">{{ data.item.customer_info.phone1 }}</small>
                </div>
              </template>

              <template #cell(location)="data">
                <div>
                  {{ data.item.governorate_name }}<br>
                  <small class="text-muted">{{ data.item.delegation_name }}</small>
                </div>
              </template>

              <template #cell(price_ttc)="data">
                <b-badge variant="warning">
                  {{ (data.item.price_ttc || 0).toFixed(3) }} DT
                </b-badge>
              </template>

              <template #cell(return_info)="data">
                <div>
                  <b-badge
                    variant="danger"
                    pill
                    class="mb-1"
                  >
                    {{ data.item.return_number }} tentative{{ data.item.return_number > 1 ? 's' : '' }}
                  </b-badge>
                  <br>
                  <small
                    v-if="data.item.return_reason"
                    class="text-danger"
                  >
                    {{ data.item.return_reason.content }}
                  </small>
                </div>
              </template>
            </b-table>

            <!-- Summary Stats -->
            <div class="border-top pt-2">
              <small class="text-muted">
                <feather-icon
                  icon="RotateCcwIcon"
                  size="12"
                  class="mr-1"
                />
                Total tentatives: {{ getOrderBreakdownValue('returned_summary', 'total_attempts') }}
              </small>
            </div>
          </b-card>
        </b-col>

        <!-- Exchange Orders Details -->
        <b-col md="4">
          <b-card
            header="Colis Échangés"
            header-bg-variant="info"
            header-text-variant="white"
          >
            <div class="mb-3">
              <h5 class="text-info">
                {{ payment.exchange_orders_count }} colis
                <small class="text-muted">({{ getOrderBreakdownValue('exchange_summary', 'total_value').toFixed(3) }} DT)</small>
              </h5>
            </div>

            <!-- Exchange Orders Table -->
            <b-table
              v-if="payment.exchange_orders && payment.exchange_orders.length > 0"
              :items="payment.exchange_orders"
              :fields="exchangeOrderFields"
              small
              striped
              hover
              responsive
              class="mb-3"
            >
              <template #cell(customer_info)="data">
                <div>
                  <strong>{{ data.item.customer_info.name }}</strong><br>
                  <small class="text-muted">{{ data.item.customer_info.phone1 }}</small>
                </div>
              </template>

              <template #cell(location)="data">
                <div>
                  {{ data.item.governorate_name }}<br>
                  <small class="text-muted">{{ data.item.delegation_name }}</small>
                </div>
              </template>

              <template #cell(price_ttc)="data">
                <b-badge variant="info">
                  {{ (data.item.price_ttc || 0).toFixed(3) }} DT
                </b-badge>
              </template>

              <template #cell(exchange_info)="data">
                <div v-if="data.item.order_metrics">
                  <small class="text-info">
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="12"
                      class="mr-1"
                    />
                    Service Premium
                  </small>
                  <br>
                  <small
                    v-if="data.item.order_metrics.is_fragile"
                    class="text-warning"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                      class="mr-1"
                    />
                    Fragile
                  </small>
                </div>
              </template>
            </b-table>

            <!-- Summary Stats -->
            <div class="border-top pt-2">
              <small class="text-muted">
                <feather-icon
                  icon="RefreshCwIcon"
                  size="12"
                  class="mr-1"
                />
                Service niveau Premium
              </small>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Financial Analysis -->
      <b-row
        v-if="payment.financial_summary"
        class="mb-4"
      >
        <b-col>
          <b-card header="Analyse Financière">
            <b-row>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-success">
                    {{ payment.financial_summary.gross_revenue.toFixed(3) }} DT
                  </h4>
                  <p class="text-muted">
                    Chiffre d'Affaires Brut
                  </p>
                </div>
              </b-col>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-info">
                    {{ payment.financial_summary.net_revenue.toFixed(3) }} DT
                  </h4>
                  <p class="text-muted">
                    Chiffre d'Affaires Net
                  </p>
                </div>
              </b-col>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-warning">
                    {{ payment.financial_summary.delivery_costs.toFixed(3) }} DT
                  </h4>
                  <p class="text-muted">
                    Coûts de Livraison
                  </p>
                </div>
              </b-col>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-danger">
                    {{ payment.financial_summary.return_losses.toFixed(3) }} DT
                  </h4>
                  <p class="text-muted">
                    Pertes Retours
                  </p>
                </div>
              </b-col>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-primary">
                    {{ payment.financial_summary.profit_margin.toFixed(2) }}%
                  </h4>
                  <p class="text-muted">
                    Marge Bénéficiaire
                  </p>
                </div>
              </b-col>
              <b-col md="2">
                <div class="text-center">
                  <h4 class="text-secondary">
                    {{ payment.financial_summary.break_even_orders }}
                  </h4>
                  <p class="text-muted">
                    Seuil de Rentabilité
                  </p>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Performance Comparison -->
      <b-row
        v-if="payment.comparative_metrics"
        class="mb-4"
      >
        <b-col md="6">
          <b-card header="Comparaison Dépôt">
            <div v-if="payment.comparative_metrics.repository_comparison && Object.keys(payment.comparative_metrics.repository_comparison).length > 0">
              <div class="mb-3">
                <b-badge
                  :variant="payment.comparative_metrics.repository_comparison.repository_rank === 'Above Average' ? 'success' : 'warning'"
                  class="mb-2"
                >
                  {{ payment.comparative_metrics.repository_comparison.repository_rank }}
                </b-badge>
              </div>
              <div class="row">
                <div class="col-6">
                  <h5 class="text-primary">
                    {{ payment.comparative_metrics.repository_comparison.delivered_vs_avg > 0 ? '+' : '' }}{{ payment.comparative_metrics.repository_comparison.delivered_vs_avg }}%
                  </h5>
                  <p class="text-muted">
                    vs Moyenne Dépôt (Livrés)
                  </p>
                </div>
                <div class="col-6">
                  <h5 class="text-info">
                    {{ payment.comparative_metrics.repository_comparison.revenue_vs_avg > 0 ? '+' : '' }}{{ payment.comparative_metrics.repository_comparison.revenue_vs_avg }}%
                  </h5>
                  <p class="text-muted">
                    vs Moyenne Dépôt (CA)
                  </p>
                </div>
              </div>
            </div>
            <div v-else>
              <p class="text-muted text-center">
                Données de comparaison non disponibles
              </p>
            </div>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card header="Comparaison Livreur">
            <div v-if="payment.comparative_metrics.driver_comparison && Object.keys(payment.comparative_metrics.driver_comparison).length > 0">
              <div class="mb-3">
                <b-badge
                  :variant="payment.comparative_metrics.driver_comparison.driver_rank === 'Above Average' ? 'success' : 'warning'"
                  class="mb-2"
                >
                  {{ payment.comparative_metrics.driver_comparison.driver_rank }}
                </b-badge>
              </div>
              <div class="row">
                <div class="col-6">
                  <h5 class="text-primary">
                    {{ payment.comparative_metrics.driver_comparison.delivered_vs_avg > 0 ? '+' : '' }}{{ payment.comparative_metrics.driver_comparison.delivered_vs_avg }}%
                  </h5>
                  <p class="text-muted">
                    vs Moyenne Livreur (Livrés)
                  </p>
                </div>
                <div class="col-6">
                  <h5 class="text-info">
                    {{ payment.comparative_metrics.driver_comparison.revenue_vs_avg > 0 ? '+' : '' }}{{ payment.comparative_metrics.driver_comparison.revenue_vs_avg }}%
                  </h5>
                  <p class="text-muted">
                    vs Moyenne Livreur (CA)
                  </p>
                </div>
              </div>
            </div>
            <div v-else>
              <p class="text-muted text-center">
                Données de comparaison non disponibles
              </p>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Recommendations -->
      <b-row
        v-if="payment.comparative_metrics && payment.comparative_metrics.improvement_suggestions"
        class="mb-4"
      >
        <b-col>
          <b-card header="Recommandations d'Amélioration">
            <div
              v-for="(suggestion, index) in payment.comparative_metrics.improvement_suggestions"
              :key="index"
              class="d-flex align-items-start mb-2"
            >
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
                class="text-primary mr-2 mt-1"
              />
              <span>{{ suggestion }}</span>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BRow, BCol, BButton, BSpinner, BAlert, BBadge, BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    BTable,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      payment: null,
      isLoading: false,
      error: null,

      // Table field definitions
      deliveredOrderFields: [
        { key: 'id', label: 'ID', class: 'text-center' },
        { key: 'customer_info', label: 'Client' },
        { key: 'location', label: 'Localisation' },
        { key: 'price_ttc', label: 'Prix TTC', class: 'text-center' },
        { key: 'delivery_metrics', label: 'Livraison', class: 'text-center' },
      ],

      returnOrderFields: [
        { key: 'id', label: 'ID', class: 'text-center' },
        { key: 'customer_info', label: 'Client' },
        { key: 'location', label: 'Localisation' },
        { key: 'price_ttc', label: 'Prix TTC', class: 'text-center' },
        { key: 'return_info', label: 'Retour', class: 'text-center' },
      ],

      exchangeOrderFields: [
        { key: 'id', label: 'ID', class: 'text-center' },
        { key: 'customer_info', label: 'Client' },
        { key: 'location', label: 'Localisation' },
        { key: 'price_ttc', label: 'Prix TTC', class: 'text-center' },
        { key: 'exchange_info', label: 'Échange', class: 'text-center' },
      ],
    }
  },
  created() {
    this.fetchPaymentKPI()
  },
  methods: {
    async fetchPaymentKPI() {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.get(`/api/payments/detail/${this.id}/`)
        this.payment = data
      } catch (error) {
        console.error('Error fetching payment KPI:', error)
        if (error.response?.status === 404) {
          this.error = 'Paiement non trouvé'
        } else if (error.response?.status === 403) {
          this.error = 'Vous n\'avez pas les permissions pour consulter ce paiement'
        } else {
          this.error = 'Erreur lors du chargement des données du paiement'
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Get value from order breakdown safely
     */
    getOrderBreakdownValue(section, key) {
      if (!this.payment || !this.payment.order_breakdown || !this.payment.order_breakdown[section]) {
        return key === 'governorates' || key === 'suppliers' ? [] : 0
      }
      return this.payment.order_breakdown[section][key] || (key === 'governorates' || key === 'suppliers' ? [] : 0)
    },
  },
}
</script>

<style lang="scss" scoped>
// Enhanced KPI cards styling
.kpi-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #e3e6f0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
}

.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
}

.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
}

.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
}

.bg-light-info {
  background-color: rgba(0, 207, 232, 0.12) !important;
}

.bg-light-secondary {
  background-color: rgba(130, 134, 139, 0.12) !important;
}

.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
}

// Enhanced table styling
.table {
  font-size: 0.875rem;

  th {
    border-top: none;
    font-weight: 600;
    background-color: #f8f9fc;
  }

  td {
    vertical-align: middle;
  }
}

// Card header styling
.card-header {
  font-weight: 600;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}
</style>
